import React from 'react';
import { Box, Grid, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { secondaryColors } from '../../../theme/themeConfig';
import {
  getCurrency,
  convertUTCToLocalTimeZoneHHMMSS
} from '../../../utils/general';
import ShowInformationIndicator from '../../../components/modals/ShowInformationIndicator';
import { formatMillisecondsToHHMMSS } from '../../../utils/calendarHelper';
import { TimeFormatEnum } from '../../../api-client/generated/api';

type UserType = {
  email: string;
  firstName: string;
  id: string;
  lastName: string;
  phoneNumber: string;
};

interface DataList {
  actualEnd: string;
  actualStart: string;
  actualTotal: number;
  clientName: string;
  closedBy?: string;
  closer?: UserType;
  companyName: string;
  day: string;
  id: number;
  laborHourCost: string;
  laborTotalCost: string;
  millisecondsWorked: string;
  pauseIterations: any[];
  pauseTimeMs: string;
  projectName: string;
  scheduledEnd: string;
  scheduledStart: string;
  scheduledTotal: number;
  startedBy?: string;
  starter?: UserType;
  status: string;
  taskName: string;
  technician: string;
  technicianInformation: UserType;
  timeFormat?: string;
  timezone?: string;
  travelTime: number;
  travelTimeInMilliseconds: string;
  type: string;
  typeText: string;
  variance: number;
  varianceActual: string;
  variancePercentage: string;
  workedTimeMs: string;
}

interface TimesheetExpandedReviewRowProps {
  dataList: DataList;
}

const TimesheetExpandedReviewRow: React.FC<TimesheetExpandedReviewRowProps> =
  function TimeSheetCollapsedView({ dataList }) {
    // Hooks
    const theme = useTheme();
    const { t } = useTranslation();

    const technicians = [
      {
        closedBy: dataList?.closedBy,
        closer: dataList?.closer,
        startedBy: dataList?.startedBy,
        starter: dataList?.starter,
        technician: dataList?.technicianInformation
      }
    ];

    // Render
    return (
      <Box
        bgcolor="#FCFCFC"
        display="flex"
        flexDirection="column"
        gap={theme.spacing(3)}
        paddingX={theme.spacing(1)}
        paddingY={theme.spacing(3)}
      >
        {/* Project information */}
        <Box>
          <Box alignItems="center" display="flex" gap={1}>
            <Typography fontSize={18} variant="primary-bold">
              {t('TIMESHEET.projectInformationTitle')}
            </Typography>
            <ShowInformationIndicator technicians={technicians} />
          </Box>
          <Grid
            container
            justifyContent="space-between"
            paddingX={theme.spacing(1)}
            paddingY={theme.spacing(3)}
            xs={12}
          >
            <Grid
              item
              xs
              display="flex"
              flexDirection="column"
              gap={theme.spacing(1)}
            >
              <Typography color={secondaryColors.SECONDARY} fontSize={14}>
                {t('TIMESHEET.taskName')}
              </Typography>
              <Typography fontSize={16} variant="primary-bold">
                {dataList.taskName}
              </Typography>
            </Grid>
            <Grid
              item
              xs
              display="flex"
              flexDirection="column"
              gap={theme.spacing(1)}
            >
              <Typography color={secondaryColors.SECONDARY} fontSize={14}>
                {t('TIMESHEET.client')}
              </Typography>
              <Typography fontSize={16} variant="primary-bold">
                {dataList.clientName}
              </Typography>
            </Grid>
            <Grid
              item
              xs
              display="flex"
              flexDirection="column"
              gap={theme.spacing(1)}
            >
              <Typography color={secondaryColors.SECONDARY} fontSize={14}>
                {t('TIMESHEET.companyName')}
              </Typography>
              <Typography fontSize={16} variant="primary-bold">
                {dataList.companyName}
              </Typography>
            </Grid>
            <Grid
              item
              xs
              display="flex"
              flexDirection="column"
              gap={theme.spacing(1)}
            >
              <Typography color={secondaryColors.SECONDARY} fontSize={14}>
                {t('TIMESHEET.laborHourCost')}
              </Typography>
              <Typography fontSize={16} variant="primary-bold">
                {getCurrency(dataList.laborHourCost)}
              </Typography>
            </Grid>
            <Grid
              item
              xs
              display="flex"
              flexDirection="column"
              gap={theme.spacing(1)}
            >
              <Typography color={secondaryColors.SECONDARY} fontSize={14}>
                {t('TIMESHEET.totalCost')}
              </Typography>
              <Typography fontSize={16} variant="primary-bold">
                {getCurrency(dataList.laborTotalCost)}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Time Track */}
        <Box>
          <Typography fontSize={18} variant="primary-bold">
            {t('TIMESHEET.timeTrackTitle')}
          </Typography>
          <Grid
            container
            justifyContent="space-between"
            paddingX={theme.spacing(1)}
            paddingY={theme.spacing(3)}
            xs={12}
          >
            <Grid
              item
              xs
              display="flex"
              flexDirection="column"
              gap={theme.spacing(1)}
            >
              <Typography color={secondaryColors.SECONDARY} fontSize={14}>
                {t('TIMESHEET.actualStart')}
              </Typography>
              <Typography fontSize={16} variant="primary-bold">
                {dataList.actualStart}
              </Typography>
            </Grid>
            <Grid
              item
              xs
              display="flex"
              flexDirection="column"
              gap={theme.spacing(1)}
            >
              <Typography color={secondaryColors.SECONDARY} fontSize={14}>
                {t('TIMESHEET.actualEnd')}
              </Typography>
              <Typography fontSize={16} variant="primary-bold">
                {dataList.actualEnd}
              </Typography>
            </Grid>
            <Grid
              item
              xs
              display="flex"
              flexDirection="column"
              gap={theme.spacing(1)}
            >
              <Typography color={secondaryColors.SECONDARY} fontSize={14}>
                {t('TIMESHEET.scheduleStart')}
              </Typography>
              <Typography fontSize={16} variant="primary-bold">
                {dataList.scheduledStart}
              </Typography>
            </Grid>
            <Grid
              item
              xs
              display="flex"
              flexDirection="column"
              gap={theme.spacing(1)}
            >
              <Typography color={secondaryColors.SECONDARY} fontSize={14}>
                {t('TIMESHEET.scheduleEnd')}
              </Typography>
              <Typography fontSize={16} variant="primary-bold">
                {dataList.scheduledEnd}
              </Typography>
            </Grid>
            <Grid
              item
              xs
              display="flex"
              flexDirection="column"
              gap={theme.spacing(1)}
            >
              <Typography color={secondaryColors.SECONDARY} fontSize={14}>
                {t('TIMESHEET.projectType')}
              </Typography>
              <Typography fontSize={16} variant="primary-bold">
                {dataList.typeText}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Pause List */}
        {dataList.pauseIterations && dataList.pauseIterations.length > 0 && (
          <Box>
            <Typography fontSize={18} variant="primary-bold">
              {t('TIMESHEET.pauseList')}
            </Typography>
            <Box paddingX={theme.spacing(1)} paddingY={theme.spacing(3)}>
              {/* Header Row */}
              <Grid
                container
                spacing={2}
                sx={{
                  borderBottom: `2px solid ${secondaryColors.SECONDARY}40`,
                  marginBottom: theme.spacing(2),
                  paddingBottom: theme.spacing(1)
                }}
              >
                <Grid item xs={3}>
                  <Typography
                    color={secondaryColors.SECONDARY}
                    fontSize={14}
                    variant="primary-bold"
                  >
                    {t('TIMESHEET.pauseReason')}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography
                    color={secondaryColors.SECONDARY}
                    fontSize={14}
                    variant="primary-bold"
                  >
                    {t('TIMESHEET.pauseStart')}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography
                    color={secondaryColors.SECONDARY}
                    fontSize={14}
                    variant="primary-bold"
                  >
                    {t('TIMESHEET.pauseEnd')}
                  </Typography>
                </Grid>
                <Grid item xs={3}>
                  <Typography
                    color={secondaryColors.SECONDARY}
                    fontSize={14}
                    variant="primary-bold"
                  >
                    {t('TIMESHEET.pauseDuration')}
                  </Typography>
                </Grid>
              </Grid>

              {/* Data Rows */}
              {dataList.pauseIterations.map((pause: any, index: number) => {
                const duration =
                  pause.startTime && pause.endTime
                    ? formatMillisecondsToHHMMSS(
                        (
                          new Date(pause.endTime).getTime() -
                          new Date(pause.startTime).getTime()
                        ).toString()
                      )
                    : '00:00:00';

                return (
                  <Grid
                    container
                    key={pause.id || index}
                    spacing={2}
                    sx={{
                      '&:hover': {
                        backgroundColor: `${secondaryColors.SECONDARY}05`
                      },
                      borderBottom: `1px solid ${secondaryColors.SECONDARY}10`,
                      paddingY: theme.spacing(1)
                    }}
                  >
                    <Grid item xs={3}>
                      <Typography fontSize={14}>
                        {pause.reason || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography fontSize={14}>
                        {pause.startTime
                          ? convertUTCToLocalTimeZoneHHMMSS(
                              pause.startTime,
                              dataList.timezone,
                              // eslint-disable-next-line no-underscore-dangle
                              dataList.timeFormat === TimeFormatEnum._24h
                            )
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography fontSize={14}>
                        {pause.endTime
                          ? convertUTCToLocalTimeZoneHHMMSS(
                              pause.endTime,
                              dataList.timezone,
                              // eslint-disable-next-line no-underscore-dangle
                              dataList.timeFormat === TimeFormatEnum._24h
                            )
                          : 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={3}>
                      <Typography fontSize={14}>{duration}</Typography>
                    </Grid>
                  </Grid>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    );
  };

export default TimesheetExpandedReviewRow;
